/**
 * Fetches for screen 06. Same shape as `src/api.ts` — bearer token on every
 * call, a typed error so a 403 can read as "your role does not allow this"
 * rather than "something went wrong".
 */

import { DEFAULT_PREP_TABS, type BatchRun, type PrepRecurrence, type PrepTask } from "@baros/core";
import { request as shared, type Me } from "../api.js";


/** A task as the screen sees it: the definition plus tonight's tick. */
export interface PrepRow extends PrepTask {
  readonly done: boolean;
  readonly doneAt: string | null;
  readonly doneBy: string | null;
}

export interface PrepTabGroup {
  readonly tab: string;
  readonly tasks: readonly PrepRow[];
  readonly doneCount: number;
}

export interface PrepDay {
  readonly serviceDate: string;
  readonly tabs: readonly PrepTabGroup[];
}

/** Everything a manager may set. `taskId` is the server's. */
export type PrepTaskBody = Omit<PrepTask, "taskId">;

export const EMPTY_TASK: PrepTaskBody = {
  tab: DEFAULT_PREP_TABS[0] ?? "Opening",
  title: "",
  note: "",
  produces: "",
  yield: "",
  leadTime: "",
  dueAt: "",
  recurrence: null,
  sortOrder: 0,
};

export const RECURRENCES: readonly (PrepRecurrence | null)[] = [null, "nightly", "weekly"];

async function request<T>(
  token: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  // The shared client: it refreshes an expired token once and ends
  // the session when it cannot (see `request` in src/api.ts).
  return shared<T>(token, method, path, body);
}

const seg = encodeURIComponent;

/** What the venue has made, and what it holds of everything, by ingredient id. */
export interface VenueBatches {
  readonly runs: readonly BatchRun[];
  readonly heldMl: Readonly<Record<string, number>>;
}

export const prepApi = {
  batches: (token: string, venueId: string) =>
    request<VenueBatches>(token, "GET", `/v1/venues/${seg(venueId)}/batches`),

  /** Record a batch made: takes what goes in it off the shelf and adds the batch. */
  makeBatch: (token: string, venueId: string, batchId: string, volumeMl: number) =>
    request<{ run: BatchRun; notCounted: readonly string[] }>(
      token,
      "POST",
      `/v1/venues/${seg(venueId)}/batches/${seg(batchId)}/make`,
      { volumeMl },
    ),

  day: (token: string, venueId: string, date: string) =>
    request<PrepDay>(token, "GET", `/v1/venues/${seg(venueId)}/prep?date=${seg(date)}`),

  create: (token: string, venueId: string, body: PrepTaskBody) =>
    request<{ task: PrepTask }>(token, "POST", `/v1/venues/${seg(venueId)}/prep`, body),

  save: (token: string, venueId: string, fromTab: string, taskId: string, body: PrepTaskBody) =>
    request<{ task: PrepTask }>(
      token,
      "PUT",
      `/v1/venues/${seg(venueId)}/prep/${seg(fromTab)}/${seg(taskId)}`,
      body,
    ),

  remove: (token: string, venueId: string, tab: string, taskId: string) =>
    request<{ deleted: { tab: string; taskId: string } }>(
      token,
      "DELETE",
      `/v1/venues/${seg(venueId)}/prep/${seg(tab)}/${seg(taskId)}`,
    ),

  setDone: (
    token: string,
    venueId: string,
    tab: string,
    taskId: string,
    date: string,
    done: boolean,
  ) =>
    request<{ taskId: string; done: boolean }>(
      token,
      "PUT",
      `/v1/venues/${seg(venueId)}/prep/${seg(tab)}/${seg(taskId)}/done`,
      { date, done },
    ),
};

/**
 * The org's prep tabs out of `/v1/me`.
 *
 * `/v1/me` returns an empty list for an org that has never configured its
 * tabs, so that case falls back to the defaults, which is also what the API
 * does when it files a task.
 */
export function prepTabsFrom(me: Me): string[] {
  const named = me.org.settings.prepTabs.filter((t) => t.trim() !== "");
  return named.length > 0 ? named : [...DEFAULT_PREP_TABS];
}
